import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import {
  Bell,
  UserPlus,
  UserCheck,
  RefreshCw,
  ShoppingBag,
  Calendar,
  Check,
  Trash2,
  ArrowLeft,
  Loader2,
  CheckCheck,
  MessageCircle,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NOTIF_ICONS = {
  connection_request: { icon: UserPlus, color: "text-blue-400 bg-blue-500/20" },
  connection_accepted: { icon: UserCheck, color: "text-emerald-400 bg-emerald-500/20" },
  exchange_request: { icon: RefreshCw, color: "text-purple-400 bg-purple-500/20" },
  exchange_accepted: { icon: Check, color: "text-emerald-400 bg-emerald-500/20" },
  exchange_scheduled: { icon: Calendar, color: "text-blue-400 bg-blue-500/20" },
  exchange_reminder: { icon: Bell, color: "text-yellow-400 bg-yellow-500/20" },
  service_order: { icon: ShoppingBag, color: "text-[#FD5564] bg-[#FD5564]/20" },
  message: { icon: MessageCircle, color: "text-white bg-white/20" },
  default: { icon: Bell, color: "text-white/70 bg-white/10" },
};

const NotificationsPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/notifications?Authorization=Bearer ${token}`);
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
      toast.error("Erro ao carregar notificações");
    } finally {
      setLoading(false);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put(
        `${API}/notifications/read?Authorization=Bearer ${token}`,
        []
      );
      setNotifications(notifications.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
      toast.success("Todas marcadas como lidas");
    } catch (error) {
      toast.error("Erro ao marcar como lidas");
    }
  };

  const deleteNotification = async (notifId) => {
    try {
      await axios.delete(`${API}/notifications/${notifId}?Authorization=Bearer ${token}`);
      setNotifications(notifications.filter(n => n.id !== notifId));
      toast.success("Notificação removida");
    } catch (error) {
      toast.error("Erro ao remover notificação");
    }
  };

  const handleNotificationClick = async (notif) => {
    // Mark as read
    if (!notif.is_read) {
      try {
        await axios.put(
          `${API}/notifications/read?Authorization=Bearer ${token}`,
          [notif.id]
        );
        setNotifications(notifications.map(n => 
          n.id === notif.id ? { ...n, is_read: true } : n
        ));
        setUnreadCount(Math.max(0, unreadCount - 1));
      } catch (error) {
        console.error("Failed to mark notification as read:", error);
      }
    }

    // Navigate if there's an action URL
    if (notif.action_url) {
      navigate(notif.action_url);
    }
  };

  const groupNotificationsByDate = (notifs) => {
    const groups = {};
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    notifs.forEach(notif => {
      const date = new Date(notif.created_at);
      date.setHours(0, 0, 0, 0);

      let key;
      if (date.getTime() === today.getTime()) {
        key = "Hoje";
      } else if (date.getTime() === yesterday.getTime()) {
        key = "Ontem";
      } else {
        key = date.toLocaleDateString('pt-BR', { day: 'numeric', month: 'long' });
      }

      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(notif);
    });

    return groups;
  };

  const groupedNotifications = groupNotificationsByDate(notifications);

  const NotificationCard = ({ notification }) => {
    const notifType = NOTIF_ICONS[notification.type] || NOTIF_ICONS.default;
    const Icon = notifType.icon;

    return (
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: 20 }}
        className={`
          bg-white/5 border rounded-xl p-4 cursor-pointer transition-all
          ${notification.is_read ? 'border-white/5' : 'border-[#FD5564]/30 bg-[#FD5564]/5'}
          hover:border-white/20
        `}
        onClick={() => handleNotificationClick(notification)}
      >
        <div className="flex items-start gap-4">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${notifType.color}`}>
            <Icon className="w-5 h-5" />
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className={`font-medium ${notification.is_read ? 'text-white/80' : 'text-white'}`}>
                  {notification.title}
                </div>
                <div className="text-sm text-white/50 mt-0.5">
                  {notification.message}
                </div>
              </div>
              
              {!notification.is_read && (
                <div className="w-2 h-2 rounded-full bg-[#FD5564] flex-shrink-0 mt-2" />
              )}
            </div>
            
            <div className="flex items-center justify-between mt-2">
              <span className="text-xs text-white/30">
                {formatDistanceToNow(new Date(notification.created_at), { 
                  addSuffix: true, 
                  locale: ptBR 
                })}
              </span>
              
              <Button
                variant="ghost"
                size="icon"
                className="w-8 h-8 text-white/30 hover:text-red-400 hover:bg-red-500/20"
                onClick={(e) => {
                  e.stopPropagation();
                  deleteNotification(notification.id);
                }}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-[#080808] text-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 px-4 py-4 bg-[#080808]/80 backdrop-blur-md border-b border-white/5">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/discovery")}
              className="text-white/70"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold">Notificações</h1>
              {unreadCount > 0 && (
                <Badge className="bg-[#FD5564] text-white border-0">
                  {unreadCount}
                </Badge>
              )}
            </div>
          </div>
          
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={markAllAsRead}
              className="text-white/70"
            >
              <CheckCheck className="w-4 h-4 mr-1" />
              Marcar lidas
            </Button>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-24 px-4">
        <div className="max-w-2xl mx-auto">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-[#FD5564]" />
            </div>
          ) : notifications.length > 0 ? (
            <div className="space-y-6">
              {Object.entries(groupedNotifications).map(([date, notifs]) => (
                <div key={date}>
                  <h2 className="text-sm font-medium text-white/50 mb-3 uppercase tracking-wide">
                    {date}
                  </h2>
                  <div className="space-y-3">
                    <AnimatePresence>
                      {notifs.map((notif) => (
                        <NotificationCard key={notif.id} notification={notif} />
                      ))}
                    </AnimatePresence>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <Bell className="w-12 h-12 text-white/20 mx-auto mb-4" />
              <p className="text-white/50 mb-2">Nenhuma notificação</p>
              <p className="text-white/30 text-sm">
                Você será notificado sobre novas conexões, trocas e pedidos
              </p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default NotificationsPage;
